

















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// Petit type interne pour manipuler les rectangles en mémoire locale
type KBRect = { x: number; y: number; w: number; h: number };
type BoxName = 'start' | 'end';
type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

// Les **champs plats** que le composant lira/écrira via v-model
export type FlatKB = {
  kenburnsEnabled?: boolean;
  kbStartX?: number;
  kbStartY?: number;
  kbStartW?: number;
  kbStartH?: number;
  kbEndX?: number;
  kbEndY?: number;
  kbEndW?: number;
  kbEndH?: number;
  kbEasing?: string;
  outARMode?: string; // 'image' | '16:9' | '4:3' | '1:1' | '9:16' | '239:100' | 'custom'
  outARCustom?: string; // requis si mode 'custom' (ex: "239:100" ou "2.39:1")
  // (Optionnel) n’importe quels autres champs du parent : on les propage sans les toucher
  [k: string]: any;
};

@Component
export default class KenBurnsEditor extends Vue {
  @Prop({ type: String, required: false, default: '' }) readonly src!: string;
  @Prop({ type: Number, required: false, default: 0 }) readonly rotate!: number;

  // ⬇️ ⬅️ IMPORTANT : la valeur v-model est maintenant **plate**
  @Prop({ type: Object, required: true }) readonly value!: FlatKB;

  // État local pour l’édition (toujours pratique pour l’UI)
  local = {
    enabled: false,
    start: { x: 0, y: 0, w: 1, h: 1 } as KBRect,
    end: { x: 0.1, y: 0.1, w: 0.8, h: 0.8 } as KBRect,
    easing: 'linear' as string,
    outAR: 1, // <-- ratio de sortie W/H (ex: 16/9)
    outARMode: 'image' as string,
    outARCustom: '' as string,
  };

  imgW = 4; // largeur native
  imgH = 3; // hauteur native
  playing = false;
  previewFrame = 0;
  previewRAF: number | null = null;
  previewDuration = 3000; // ms
  private resizeObserver?: ResizeObserver;

  easingOptions = [
    { value: 'linear', text: 'Linéaire' },
    { value: 'ease-in', text: 'Ease In' },
    { value: 'ease-out', text: 'Ease Out' },
    { value: 'ease-in-out', text: 'Ease In-Out' },
    { value: 'ease-in-cubic', text: 'Cubic In' },
    { value: 'ease-out-cubic', text: 'Cubic Out' },
    { value: 'ease-in-out-cubic', text: 'Cubic In-Out' },
  ];

  get rotated90() {
    return Math.abs(this.rotate % 180) === 90;
  }
  get displayW() {
    return this.rotated90 ? this.imgH : this.imgW;
  }
  get displayH() {
    return this.rotated90 ? this.imgW : this.imgH;
  }
  get displayAR() {
    return this.displayW / Math.max(1, this.displayH);
  } // width/height

  // Le style du wrapper : 100% de large, hauteur auto via aspect-ratio
  get wrapStyle() {
    // Format "w / h" = le plus précis (aucun arrondi)
    return { width: '100%', aspectRatio: `${this.displayW} / ${this.displayH}` };
  }

  get outAREffective(): number {
    return this.local.outARMode === 'image' ? this.displayAR : this.local.outAR || 1;
  }

  get ratioText() {
    const r = this.outAREffective;
    return `${r.toFixed(3)} (sortie)`;
  }

  // ---------- Preview / Canvas (inchangé) ----------
  async playPreview() {
    if (!this.src || !this.local.start || !this.local.end) return;
    const wrap = this.$refs.wrap as HTMLElement;
    const canvas = this.$refs.canvas as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      this.playing = true;
      this.previewFrame = 0;
      wrap.classList.add('playing');

      const imgEl = this.$refs.imgEl as HTMLImageElement | undefined;
      if (!imgEl) throw new Error('Image introuvable');

      if (!imgEl) throw new Error('Image introuvable');
      if (!imgEl.complete || imgEl.naturalWidth === 0) {
        await new Promise<void>((resolve, reject) => {
          imgEl.addEventListener('load', () => resolve(), { once: true });
          imgEl.addEventListener('error', () => reject(new Error('Chargement image échoué')), { once: true });
        });
      }

      const dpr = window.devicePixelRatio || 1;
      const wrapRect = wrap.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(wrapRect.width * dpr));
      canvas.height = Math.max(1, Math.round(wrapRect.height * dpr));
      canvas.style.width = `${wrapRect.width}px`;
      canvas.style.height = `${wrapRect.height}px`;

      const iw = imgEl.naturalWidth || imgEl.width;
      const ih = imgEl.naturalHeight || imgEl.height;

      const start = this.local.start!;
      const end = this.local.end!;
      const startTime = performance.now();

      const drawFrame = (now: number) => {
        if (!this.playing) return;

        const t = Math.min(1, (now - startTime) / this.previewDuration);
        const easedT = this.applyEasing(t, this.local.easing ?? 'linear');
        this.previewFrame = easedT;

        const L = (a: number, b: number) => a + (b - a) * easedT;
        const cur = { x: L(start.x, end.x), y: L(start.y, end.y), w: L(start.w, end.w), h: L(start.h, end.h) };

        const sx = cur.x * iw,
          sy = cur.y * ih,
          sw = cur.w * iw,
          sh = cur.h * ih;

        const dest = { x: 0, y: 0, w: canvas.width, h: canvas.height };

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.fillStyle = '#0b1a2a'; // masque l'<img> derrière
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        ctx.drawImage(imgEl, sx, sy, sw, sh, dest.x, dest.y, dest.w, dest.h);

        if (t < 1) this.previewRAF = requestAnimationFrame(drawFrame);
        else {
          this.stopPreview();
          this.resetCanvasPreview(ctx, imgEl);
        }
      };

      this.previewRAF = requestAnimationFrame(drawFrame);
    } catch (err) {
      console.error('[KB] Aperçu impossible :', err);
      this.stopPreview();
    }
  }

  private isAlive() {
    return !!this.$el && !(this as any)._isBeingDestroyed && !(this as any)._isDestroyed;
  }

  resetCanvasPreview(_: CanvasRenderingContext2D, __: HTMLImageElement) {
    // Hors lecture, on ne veut rien dessiner dans le canvas
    this.clearCanvas();
  }

  parseARString(s: string): number | null {
    if (!s) return null;
    const t = s.trim().toLowerCase().replace('x', ':').replace('/', ':');
    const parts = t.split(':').filter(Boolean);
    // "2.39" ou "2.39:1"
    if (parts.length === 1) {
      const v = parseFloat(parts[0]);
      return Number.isFinite(v) && v > 0 ? v : null;
    }
    if (parts.length === 2) {
      const a = parseFloat(parts[0]);
      const b = parseFloat(parts[1]);
      if (Number.isFinite(a) && Number.isFinite(b) && b > 0) return a / b;
    }
    return null;
  }

  onCustomARChange() {
    if (this.local.outARMode !== 'custom') return;
    const r = this.parseARString(this.local.outARCustom);
    if (r) this.setOutputAR(r);
    this.emitAll();
  }

  stopPreview() {
    if (this.previewRAF) cancelAnimationFrame(this.previewRAF);
    this.previewRAF = null;
    this.playing = false;
    (this.$refs.wrap as HTMLElement | undefined)?.classList.remove('playing');
    this.clearCanvas();
  }

  applyEasing(t: number, mode: string): number {
    switch (mode) {
      case 'linear':
        return t;
      case 'ease-in':
        return t * t;
      case 'ease-out':
        return t * (2 - t);
      case 'ease-in-out':
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      case 'ease-in-cubic':
        return t * t * t;
      case 'ease-out-cubic':
        return --t * t * t + 1;
      case 'ease-in-out-cubic':
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      default:
        return t;
    }
  }

  onImgLoad(e: Event) {
    const img = e.target as HTMLImageElement;
    this.imgW = img.naturalWidth || img.width || 1;
    this.imgH = img.naturalHeight || img.height || 1;

    this.syncRectsToOutAR(); // (image OU ratio fixe)
    this.emitAll(); // ✅ important quand mode = 'image'

    this.$nextTick(() => {
      if (!this.isAlive()) return;
      this.resizeCanvasToWrap();
    });
  }

  clearCanvas() {
    const canvas = this.$refs.canvas as HTMLCanvasElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  setOutputAR(ar: number) {
    this.local.outAR = Math.max(0.05, ar); // actif uniquement si mode ≠ 'image'
    this.syncRectsToOutAR();
    this.emitAll();
    if (this.playing)
      this.$nextTick(() => {
        if (!this.isAlive()) return;
        this.resizeCanvasToWrap();
      });
  }

  syncRectsToOutAR() {
    const ar = this.outAREffective;
    this.applyARToRect(this.local.start, ar);
    this.applyARToRect(this.local.end, ar);
  }

  applyARToRect(r: KBRect, ar: number) {
    const imgAR = this.displayAR; // iw/ih
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;

    // impose ratio
    r.h = r.w * (imgAR / ar);

    // si ça dépasse l’image, on réduit (contain)
    if (r.h > 1) {
      // trop haut -> réduire via h
      r.h = 1;
      r.w = (ar / imgAR) * r.h; // inverse cohérent
    }
    if (r.w > 1) {
      // trop large -> réduire via w
      r.w = 1;
      r.h = r.w * (imgAR / ar); // réapplique la formule
    }

    // recale au centre initial
    r.x = cx - r.w / 2;
    r.y = cy - r.h / 2;

    // clamp dans l’image
    if (r.x < 0) r.x = 0;
    if (r.y < 0) r.y = 0;
    if (r.x + r.w > 1) r.x = 1 - r.w;
    if (r.y + r.h > 1) r.y = 1 - r.h;
  }

  resizeCanvasToWrap() {
    const imgEl = this.$refs.imgEl as HTMLImageElement;
    const canvas = this.$refs.canvas as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    const wrap = this.$refs.wrap as HTMLElement;
    if (!imgEl || !ctx || !imgEl.complete || imgEl.naturalWidth === 0) return;

    const wrapRect = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const ar = this.displayAR; // ratio du wrapper (image)
    const targetWidth = wrapRect.width;
    const targetHeight = targetWidth / ar;

    canvas.width = Math.round(targetWidth * dpr);
    canvas.height = Math.round(targetHeight * dpr);
    canvas.style.width = `${targetWidth}px`;
    canvas.style.height = `${targetHeight}px`;

    // ⬇️ Ne rien dessiner si on n’est pas en lecture
    if (!this.playing) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // (en lecture : on dessine le frame courant – cf. playPreview)
    const s = this.local.start!;
    const iw = this.imgW,
      ih = this.imgH;
    const sx = s.x * iw,
      sy = s.y * ih,
      sw = s.w * iw,
      sh = s.h * ih;
    const dest = { x: 0, y: 0, w: canvas.width, h: canvas.height };

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgEl, sx, sy, sw, sh, dest.x, dest.y, dest.w, dest.h);
  }

  get imgStyle() {
    return this.rotate ? { transform: `rotate(${this.rotate}deg)` } : {};
  }
  handles: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

  mounted() {
    this.hardSyncFromValue();
    this.$nextTick(() => {
      this.measureRatio();
      this.resizeCanvasToWrap();
    });
    const wrap = this.$refs.wrap as HTMLElement;
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.playing) this.resizeCanvasToWrap();
    });
    this.resizeObserver.observe(wrap);
    window.addEventListener('resize', this.resizeCanvasToWrap);
  }
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvasToWrap);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    // Annule toute animation en cours
    this.stopPreview();

    // Si un drag est en cours, nettoyer proprement
    if (this.dragState) {
      this.dragState = null;
    }
  }

  @Watch('value', { deep: true })
  onValueChanged() {
    if (this.dragState) return; // pas de resync pendant le drag
    this.softSyncFromValue();
  }
  @Watch('src')
  onSrcChanged() {
    this.$nextTick(() => {
      if (!this.isAlive()) return;
      this.measureRatio();
      this.resizeCanvasToWrap();
    });
  }

  // ---------- Sync FLAT ⇄ LOCAL ----------
  hardSyncFromValue() {
    const v = (this.value || {}) as FlatKB;

    this.local.enabled = !!v.kenburnsEnabled;
    this.local.easing = v.kbEasing || 'linear';

    this.local.start = {
      x: v.kbStartX ?? 0,
      y: v.kbStartY ?? 0,
      w: v.kbStartW ?? 1,
      h: v.kbStartH ?? 1,
    };
    this.local.end = {
      x: v.kbEndX ?? 0.1,
      y: v.kbEndY ?? 0.1,
      w: v.kbEndW ?? 0.8,
      h: v.kbEndH ?? 0.8,
    };

    // 🔹 nouveaux champs
    this.local.outARMode = v.outARMode || 'image';
    this.local.outARCustom = v.outARCustom || '';

    // Fixe le ratio numérique local.outAR si mode ≠ 'image'
    if (this.local.outARMode !== 'image') {
      const named: Record<string, number> = {
        '16:9': 16 / 9,
        '4:3': 4 / 3,
        '1:1': 1,
        '9:16': 9 / 16,
        '239:100': 2.39,
      };
      const r =
        this.local.outARMode === 'custom' ? this.parseARString(this.local.outARCustom) : named[this.local.outARMode];
      if (r) this.local.outAR = r;
    }

    this.clampRect(this.local.start);
    this.clampRect(this.local.end);
  }

  softSyncFromValue() {
    // on re-synchronise uniquement si différence
    const flatNow = this.makeFlatFromLocal(this.value);
    const flatFromLocal = this.makeFlatFromLocal();
    if (JSON.stringify(flatNow) !== JSON.stringify(flatFromLocal)) {
      this.hardSyncFromValue();
    }
  }

  // Construit un objet **plat** à émettre, en conservant les autres champs existants de `value`
  private makeFlatFromLocal(base?: FlatKB) {
    const out: FlatKB = Object.assign({}, base || this.value || {});
    out.kenburnsEnabled = !!this.local.enabled;
    out.kbEasing = this.local.easing;
    out.kbStartX = this.local.start.x;
    out.kbStartY = this.local.start.y;
    out.kbStartW = this.local.start.w;
    out.kbStartH = this.local.start.h;
    out.kbEndX = this.local.end.x;
    out.kbEndY = this.local.end.y;
    out.kbEndW = this.local.end.w;
    out.kbEndH = this.local.end.h;

    // 🔹 nouveaux champs
    out.outARMode = this.local.outARMode;
    out.outARCustom = this.local.outARCustom || undefined;

    return out;
  }

  emitAll = () => {
    this.$emit('input', this.makeFlatFromLocal());
    this.$emit('change', this.makeFlatFromLocal());
  };

  resetRects() {
    this.local.start = { x: 0, y: 0, w: 1, h: 1 };
    this.local.end = { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };
    this.clampRect(this.local.start);
    this.clampRect(this.local.end);
    this.emitAll();
  }

  // ---------- Drag/resize (inchangé) ----------
  dragState: null | {
    box: BoxName;
    mode: 'move' | 'resize';
    handle?: Handle;
    startRect: KBRect;
    startX: number;
    startY: number;
    wrapW: number;
    wrapH: number;
  } = null;

  onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const carrier = (target.closest && target.closest('[data-action]')) as HTMLElement | null;
    if (!carrier) return;

    const box = carrier.dataset.box as BoxName;
    const action = carrier.dataset.action!;
    const wrap = this.$refs.wrap as HTMLElement;
    const rect = wrap.getBoundingClientRect();

    const clientX = e.clientX,
      clientY = e.clientY;

    this.dragState = {
      box,
      mode: action.startsWith('resize') ? 'resize' : 'move',
      handle: action.startsWith('resize') ? (action.split(':')[1] as Handle) : undefined,
      startRect: JSON.parse(JSON.stringify(this.local[box]!)),
      startX: clientX - rect.left,
      startY: clientY - rect.top,
      wrapW: rect.width,
      wrapH: rect.height,
    };

    target.setPointerCapture(e.pointerId);
    target.addEventListener('pointermove', this.onPointerMove);
    target.addEventListener('pointerup', this.onPointerUp);
  };

  onPointerMove = (e: PointerEvent) => {
    if (!this.dragState) return;
    e.preventDefault();

    const ds = this.dragState;
    const wrap = this.$refs.wrap as HTMLElement;
    const rect = wrap.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const boxRect = this.local[ds.box]!;

    if (ds.mode === 'move') {
      const dx = (x - ds.startX) / ds.wrapW;
      const dy = (y - ds.startY) / ds.wrapH;
      boxRect.x = ds.startRect.x + dx;
      boxRect.y = ds.startRect.y + dy;
    } else {
      const ar = this.outAREffective;
      const imgAR = this.displayAR;
      const rx = ds.startRect.x,
        ry = ds.startRect.y,
        rw = ds.startRect.w,
        rh = ds.startRect.h;
      const cx = rx + rw / 2,
        cy = ry + rh / 2;
      const dx = (x - ds.startX) / ds.wrapW;
      const dy = (y - ds.startY) / ds.wrapH;
      let scale = 1;
      if (['e', 'w'].includes(ds.handle!)) scale = 1 + ((ds.handle === 'e' ? dx : -dx) * 2) / rw;
      else if (['n', 's'].includes(ds.handle!)) scale = 1 + ((ds.handle === 's' ? dy : -dy) * 2) / rh;
      else {
        const s1 = 1 + ((['ne', 'e', 'se'].includes(ds.handle!) ? dx : -dx) * 2) / rw;
        const s2 = 1 + ((['se', 's', 'sw'].includes(ds.handle!) ? dy : -dy) * 2) / rh;
        scale = (s1 + s2) / 2;
      }
      scale = Math.max(0.1, Math.min(10, scale));
      boxRect.w = rw * scale;
      boxRect.h = boxRect.w * (imgAR / ar);
      boxRect.x = cx - boxRect.w / 2;
      boxRect.y = cy - boxRect.h / 2;
    }

    this.clampRect(boxRect);
    this.emitAll();
  };

  onPointerUp = (e: PointerEvent) => {
    if (!this.dragState) return;
    const target = e.target as HTMLElement;
    target.releasePointerCapture(e.pointerId);
    target.removeEventListener('pointermove', this.onPointerMove);
    target.removeEventListener('pointerup', this.onPointerUp);
    this.dragState = null;
  };

  fitDestContain(cw: number, ch: number, ar: number) {
    // ar = W/H à afficher
    const cwAr = cw / ch;
    if (cwAr > ar) {
      // canvas trop large -> bandes verticales
      const h = ch;
      const w = Math.round(h * ar);
      const x = Math.round((cw - w) / 2);
      const y = 0;
      return { x, y, w, h };
    } else {
      // canvas trop haut -> bandes horizontales
      const w = cw;
      const h = Math.round(w / ar);
      const x = 0;
      const y = Math.round((ch - h) / 2);
      return { x, y, w, h };
    }
  }

  // ---------- Utils ----------
  measureRatio = () => {
    if (!this.isAlive()) return;
    const img = this.$refs.imgEl as HTMLImageElement | undefined;
    if (!img) return;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w && h) {
      this.imgW = w;
      this.imgH = h;
    }
  };

  rectStyle(r?: KBRect) {
    if (!r) return {};
    return {
      left: r.x * 100 + '%',
      top: r.y * 100 + '%',
      width: r.w * 100 + '%',
      height: r.h * 100 + '%',
    };
  }

  clampRect(r: KBRect) {
    this.applyARToRect(r, this.outAREffective);
  }

  @Watch('rotate')
  onRotateChanged() {
    if (this.local.outARMode === 'image') {
      this.syncRectsToOutAR();
      this.$nextTick(() => {
        if (!this.isAlive()) return;
        this.resizeCanvasToWrap();
      });
    }
  }
  @Watch('local.outARCustom')
  onLocalCustomARChanged(val: string) {
    if (this.local.outARMode !== 'custom') return;
    const r = this.parseARString(val);
    if (r) this.setOutputAR(r);
  }

  @Watch('local.outARMode')
  onOutARModeChange(val: string) {
    if (val === 'image') {
      // suit le ratio de l’image affichée
      this.local.outAR = this.displayAR; // sécurité interne
      this.syncRectsToOutAR();
      this.emitAll();
      return;
    }
    if (val === '16:9') return this.setOutputAR(16 / 9);
    if (val === '4:3') return this.setOutputAR(4 / 3);
    if (val === '1:1') return this.setOutputAR(1);
    if (val === '9:16') return this.setOutputAR(9 / 16);
    if (val === '239:100') return this.setOutputAR(2.39);

    if (val === 'custom') {
      const r = this.parseARString(this.local.outARCustom || '');
      this.setOutputAR(r || this.displayAR);
      this.emitAll();
    }
  }
}
